import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { addSleepEntry, getSleepSummary, updateSleepEntry, deleteSleepEntry } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import { useSmartComment } from '../hooks/useSmartComment';
import { MoonWidget } from '../components/MoonWidget';
import { useAuth } from '../contexts/AuthContext';
import { CalendarStrip } from '../components/CalendarStrip';
import { WeatherTipsCard } from '../components/WeatherTipsCard';
import { PeriodPicker } from '../components/PeriodPicker';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { Moon, Plus, Clock, Star, BedDouble, Pencil, Trash2, Check, X, ChevronDown, Sparkles, Loader2 } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { de, enUS } from 'date-fns/locale';

const NANOUK_SLEEPING = 'https://images.unsplash.com/photo-1767381604151-bae00f2fb337?w=200&h=200&fit=crop';

export default function Sleep() {
    const { language } = useLanguage();
    const { user } = useAuth();
    const [summary, setSummary] = useState(null);
    const [summaryDays, setSummaryDays] = useState(14);
    const [loading, setLoading] = useState(true);
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [quickLogOpen, setQuickLogOpen] = useState(false);
    const [editingEntry, setEditingEntry] = useState(null);
    const [showHistory, setShowHistory] = useState(false);
    const [autoFillInfo, setAutoFillInfo] = useState(null); // { created, entry }
    
    const API = process.env.REACT_APP_BACKEND_URL;
    
    // Quick log form
    const [quickForm, setQuickForm] = useState({
        sleep_time: user?.default_sleep_time || '23:00',
        wake_time: user?.default_wake_time || '07:00',
        quality_rating: user?.default_sleep_quality || 7,
    });
    const [quickLoading, setQuickLoading] = useState(false);
    const { fetchComment } = useSmartComment();

    const sleepGoal = user?.settings?.sleep_goal_hours || 8;

    useEffect(() => {
        loadSummary();
        autofillToday();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        loadSummary();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [summaryDays]);
    
    useEffect(() => {
        setQuickForm({
            sleep_time: user?.default_sleep_time || '23:00',
            wake_time: user?.default_wake_time || '07:00',
            quality_rating: user?.default_sleep_quality || 7,
        });
    }, [user]);

    const autofillToday = async () => {
        try {
            const res = await axios.post(`${API}/api/sleep/autofill-today`, {});
            setAutoFillInfo(res.data);
            if (res.data?.created) {
                toast.success(language === 'de' ? '😴 Schlaf für heute automatisch übernommen (Standardzeiten)' : "😴 Today's sleep auto-filled from your defaults");
                loadSummary();
            }
        } catch (e) {
            console.error('autofill failed:', e);
        }
    };

    const loadSummary = async () => {
        try {
            const response = await getSleepSummary(summaryDays);
            setSummary(response.data);
        } catch (error) {
            console.error('Failed to load sleep data:', error);
        } finally {
            setLoading(false);
        }
    };

    // Calculate duration from times
    const calculateDuration = (sleepTime, wakeTime) => {
        const [sleepH, sleepM] = sleepTime.split(':').map(Number);
        const [wakeH, wakeM] = wakeTime.split(':').map(Number);
        let hours = wakeH - sleepH;
        let mins = wakeM - sleepM;
        if (hours < 0) hours += 24;
        if (mins < 0) { mins += 60; hours--; }
        return hours + mins / 60;
    };

    const handleQuickLog = async () => {
        setQuickLoading(true);
        try {
            // Use selectedDate as the wake-up date
            const wakeDate = new Date(selectedDate);
            wakeDate.setHours(0, 0, 0, 0);
            
            const [sleepHour, sleepMin] = quickForm.sleep_time.split(':').map(Number);
            const [wakeHour, wakeMin] = quickForm.wake_time.split(':').map(Number);
            
            // Sleep start is the night before the selected date
            const sleepStart = new Date(wakeDate);
            sleepStart.setDate(sleepStart.getDate() - 1); // Go back one day
            sleepStart.setHours(sleepHour, sleepMin, 0, 0);
            
            // If sleep time is in the morning (e.g., 01:00), it's actually the same day
            if (sleepHour < 12) {
                sleepStart.setDate(sleepStart.getDate() + 1);
            }
            
            // Wake time is on the selected date
            const sleepEnd = new Date(wakeDate);
            sleepEnd.setHours(wakeHour, wakeMin, 0, 0);
            
            // If wake time is in the evening (after sleep time in morning case), adjust
            if (wakeHour < sleepHour && sleepHour >= 12) {
                // Normal case: sleep at 23:00, wake at 07:00
                // sleepEnd is already correct
            }
            
            await addSleepEntry({
                sleep_start: sleepStart.toISOString(),
                sleep_end: sleepEnd.toISOString(),
                quality_rating: quickForm.quality_rating,
                notes: ''
            });
            
            toast.success(language === 'de' ? '😴 Schlaf gespeichert! +10 XP' : '😴 Sleep saved! +10 XP');
            setQuickLogOpen(false);
            loadSummary();
            fetchComment('sleep', `${estimatedDuration.toFixed(1)}h, Qualität ${quickForm.quality_rating}/10`, language);
        } catch (error) {
            console.error('Sleep save error:', error);
            toast.error(language === 'de' ? 'Fehler beim Speichern' : 'Error saving');
        } finally {
            setQuickLoading(false);
        }
    };

    const handleUpdateSleep = async (id, data) => {
        try {
            await updateSleepEntry(id, data);
            toast.success(language === 'de' ? 'Aktualisiert' : 'Updated');
            setEditingEntry(null);
            loadSummary();
        } catch (error) {
            toast.error(language === 'de' ? 'Fehler' : 'Error');
        }
    };

    const handleDeleteSleep = async (id) => {
        try {
            await deleteSleepEntry(id);
            toast.success(language === 'de' ? 'Gelöscht' : 'Deleted');
            loadSummary();
        } catch (error) {
            toast.error(language === 'de' ? 'Fehler' : 'Error');
        }
    };

    const chartData = summary?.entries?.map((entry) => {
        try {
            const sleepStart = typeof entry.sleep_start === 'string' ? parseISO(entry.sleep_start) : entry.sleep_start;
            const sleepEnd = typeof entry.sleep_end === 'string' ? parseISO(entry.sleep_end) : entry.sleep_end;
            const duration = (sleepEnd - sleepStart) / (1000 * 60 * 60);
            return {
                date: format(sleepStart, 'dd.MM', { locale: language === 'de' ? de : enUS }),
                hours: Math.round(duration * 10) / 10,
                quality: entry.quality_rating
            };
        } catch {
            return null;
        }
    }).filter(Boolean).reverse() || [];

    const getQualityColor = (q) => q >= 8 ? 'text-green-400' : q >= 5 ? 'text-yellow-400' : 'text-red-400';
    const getQualityBg = (q) => q >= 8 ? 'bg-green-500/20' : q >= 5 ? 'bg-yellow-500/20' : 'bg-red-500/20';

    const avgHours = summary?.avg_duration_hours || summary?.average_duration || 0;
    const avgQuality = summary?.avg_quality || summary?.average_quality || 0;
    const sleepProgress = (avgHours / sleepGoal) * 100;

    const estimatedDuration = calculateDuration(quickForm.sleep_time, quickForm.wake_time);

    return (
        <div className="pb-24 lg:pb-8" data-testid="sleep-page">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="max-w-4xl mx-auto space-y-4"
            >
                {/* Sticky Header */}
                <div className="sticky top-0 z-20 bg-background/95 backdrop-blur-lg -mx-4 px-4 pt-4 pb-3 border-b border-border">
                    <div className="flex items-center justify-between mb-3">
                        <h1 className="font-display text-xl lg:text-2xl font-bold flex items-center gap-2">
                            <Moon className="w-6 h-6 icon-sleep" />
                            {language === 'de' ? 'Schlaf' : 'Sleep'}
                        </h1>
                        <Button 
                            className="rounded-full h-10 px-4 bg-primary text-primary-foreground hover:bg-primary/90"
                            onClick={() => setQuickLogOpen(true)}
                            data-testid="add-sleep-btn"
                        >
                            <Plus className="w-5 h-5 mr-1" />
                            {language === 'de' ? 'Eintrag' : 'Log'}
                        </Button>
                    </div>
                    
                    <CalendarStrip 
                        selectedDate={selectedDate}
                        onDateChange={setSelectedDate}
                    />
                </div>

                <div className="px-4 space-y-4">
                    {/* Weather-aware sleep tips (heat = #1 sleep killer) */}
                    <WeatherTipsCard category="sleep" />

                    {/* Moon Phase */}
                    <MoonWidget language={language} />

                    {/* Period selector */}
                    <PeriodPicker
                        value={summaryDays}
                        onChange={setSummaryDays}
                        testid="sleep-period"
                        options={[
                            { v: 7, l: '7T' },
                            { v: 14, l: '14T' },
                            { v: 30, l: '30T' },
                            { v: 90, l: '90T' },
                        ]}
                    />

                    {/* Stats Cards */}
                    <div className="grid grid-cols-2 gap-3">
                        <Card className="module-sleep border">
                            <CardContent className="p-4">
                                <div className="flex items-center gap-2 mb-2">
                                    <Clock className="w-5 h-5 icon-sleep" />
                                    <span className="text-sm text-muted-foreground">{language === 'de' ? 'Ø Dauer' : 'Avg Duration'}</span>
                                </div>
                                <div className="text-2xl font-bold text-foreground">
                                    {avgHours.toFixed(1)}h
                                    <span className="text-sm text-muted-foreground font-normal"> / {sleepGoal}h</span>
                                </div>
                                <Progress value={Math.min(sleepProgress, 100)} className="h-2 mt-2" />
                            </CardContent>
                        </Card>

                        <Card className="module-fitness border">
                            <CardContent className="p-4">
                                <div className="flex items-center gap-2 mb-2">
                                    <Star className="w-5 h-5 icon-fitness" />
                                    <span className="text-sm text-muted-foreground">{language === 'de' ? 'Ø Qualität' : 'Avg Quality'}</span>
                                </div>
                                <div className="text-2xl font-bold text-foreground">
                                    {avgQuality.toFixed(1)}
                                    <span className="text-sm text-muted-foreground font-normal"> / 10</span>
                                </div>
                                <Progress value={avgQuality * 10} className="h-2 mt-2" />
                            </CardContent>
                        </Card>
                    </div>

                    {/* Chart */}
                    {chartData.length > 0 && (
                        <Card className="border">
                            <CardContent className="p-4">
                                <h3 className="text-sm font-medium text-muted-foreground mb-3">
                                    {(() => {
                                        const labels = language === 'de'
                                            ? { 7: 'Letzte 7 Tage', 14: 'Letzte 14 Tage', 30: 'Letzte 30 Tage', 90: 'Letzte 90 Tage' }
                                            : { 7: 'Last 7 days', 14: 'Last 14 days', 30: 'Last 30 days', 90: 'Last 90 days' };
                                        return labels[summaryDays] || `${summaryDays} ${language === 'de' ? 'Tage' : 'days'}`;
                                    })()}
                                </h3>
                                <div className="h-32">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <LineChart data={chartData}>
                                            <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={10} />
                                            <YAxis stroke="hsl(var(--muted-foreground))" fontSize={10} domain={[0, 12]} />
                                            <Tooltip 
                                                contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }}
                                                labelStyle={{ color: 'hsl(var(--muted-foreground))' }}
                                            />
                                            <Line type="monotone" dataKey="hours" stroke="#818cf8" strokeWidth={2} dot={{ fill: '#818cf8', r: 3 }} />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Sleep History */}
                    <div className="space-y-2">
                        <button
                            onClick={() => setShowHistory(!showHistory)}
                            className="w-full flex items-center justify-between p-4 module-sleep rounded-xl hover:brightness-110 transition-colors"
                        >
                            <div className="flex items-center gap-3">
                                <BedDouble className="w-5 h-5 icon-sleep" />
                                <span className="font-medium">
                                    {language === 'de' ? 'Schlafverlauf' : 'Sleep History'}
                                </span>
                                <span className="text-sm text-muted-foreground">
                                    ({summary?.entries?.length || 0} {language === 'de' ? 'Einträge' : 'entries'})
                                </span>
                            </div>
                            <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform ${showHistory ? 'rotate-180' : ''}`} />
                        </button>

                        <AnimatePresence>
                            {showHistory && (
                                <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    className="overflow-hidden"
                                >
                                    {summary?.entries?.length === 0 ? (
                                        <p className="text-center text-muted-foreground py-6">
                                            {language === 'de' ? 'Noch keine Einträge' : 'No entries yet'}
                                        </p>
                                    ) : (
                                        <div className="space-y-2">
                                            {summary?.entries?.map((entry) => {
                                                const sleepStart = typeof entry.sleep_start === 'string' ? parseISO(entry.sleep_start) : entry.sleep_start;
                                                const sleepEnd = typeof entry.sleep_end === 'string' ? parseISO(entry.sleep_end) : entry.sleep_end;
                                                const duration = ((sleepEnd - sleepStart) / (1000 * 60 * 60)).toFixed(1);
                                                
                                                if (editingEntry?.id === entry.id) {
                                                    return (
                                                        <motion.div
                                                            key={entry.id}
                                                            initial={{ opacity: 0 }}
                                                            animate={{ opacity: 1 }}
                                                            className="p-4 bg-muted rounded-xl border-2 border-primary space-y-3"
                                                        >
                                                            <div className="grid grid-cols-2 gap-3">
                                                                <div>
                                                                    <label className="text-xs text-muted-foreground">{language === 'de' ? 'Eingeschlafen' : 'Fell asleep'}</label>
                                                                    <Input
                                                                        type="datetime-local"
                                                                        value={editingEntry.sleep_start}
                                                                        onChange={(e) => setEditingEntry({...editingEntry, sleep_start: e.target.value})}
                                                                        className="h-9"
                                                                    />
                                                                </div>
                                                                <div>
                                                                    <label className="text-xs text-muted-foreground">{language === 'de' ? 'Aufgewacht' : 'Woke up'}</label>
                                                                    <Input
                                                                        type="datetime-local"
                                                                        value={editingEntry.sleep_end}
                                                                        onChange={(e) => setEditingEntry({...editingEntry, sleep_end: e.target.value})}
                                                                        className="h-9"
                                                                    />
                                                                </div>
                                                            </div>
                                                            <div>
                                                                <label className="text-xs text-muted-foreground">{language === 'de' ? 'Qualität' : 'Quality'}: {editingEntry.quality_rating}/10</label>
                                                                <Slider
                                                                    value={[editingEntry.quality_rating]}
                                                                    onValueChange={([v]) => setEditingEntry({...editingEntry, quality_rating: v})}
                                                                    min={1} max={10} step={1}
                                                                    className="py-2"
                                                                />
                                                            </div>
                                                            <div className="flex gap-2">
                                                                <Button variant="outline" size="sm" onClick={() => setEditingEntry(null)} className="flex-1">
                                                                    <X className="w-4 h-4 mr-1" /> {language === 'de' ? 'Abbrechen' : 'Cancel'}
                                                                </Button>
                                                                <Button size="sm" onClick={() => handleUpdateSleep(entry.id, {
                                                                    sleep_start: new Date(editingEntry.sleep_start).toISOString(),
                                                                    sleep_end: new Date(editingEntry.sleep_end).toISOString(),
                                                                    quality_rating: editingEntry.quality_rating
                                                                })} className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90">
                                                                    <Check className="w-4 h-4 mr-1" /> {language === 'de' ? 'Speichern' : 'Save'}
                                                                </Button>
                                                            </div>
                                                        </motion.div>
                                                    );
                                                }
                                                
                                                return (
                                                    <div
                                                        key={entry.id}
                                                        className="flex items-center gap-3 p-4 bg-card rounded-xl hover:bg-muted transition-colors cursor-pointer group"
                                                        onClick={() => setEditingEntry({
                                                            ...entry,
                                                            sleep_start: (typeof entry.sleep_start === 'string' ? entry.sleep_start : entry.sleep_start.toISOString()).slice(0, 16),
                                                            sleep_end: (typeof entry.sleep_end === 'string' ? entry.sleep_end : entry.sleep_end.toISOString()).slice(0, 16)
                                                        })}
                                                    >
                                                        <div className={`w-10 h-10 rounded-full ${getQualityBg(entry.quality_rating)} flex items-center justify-center`}>
                                                            <Moon className={`w-5 h-5 ${getQualityColor(entry.quality_rating)}`} />
                                                        </div>
                                                        <div className="flex-1">
                                                            <p className="font-medium text-foreground flex items-center gap-1.5 flex-wrap">
                                                                {format(sleepStart, 'EEEE, d. MMM', { locale: language === 'de' ? de : enUS })}
                                                                {entry.auto_filled && (
                                                                    <span
                                                                        className="text-[9px] px-1.5 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20 inline-flex items-center gap-0.5"
                                                                        title={language === 'de' ? 'Automatisch übernommen aus deinen Standardzeiten' : 'Auto-filled from your defaults'}
                                                                        data-testid={`sleep-autofilled-badge-${entry.id}`}
                                                                    >
                                                                        <Sparkles className="w-2.5 h-2.5" /> {language === 'de' ? 'Auto' : 'Auto'}
                                                                    </span>
                                                                )}
                                                            </p>
                                                            <p className="text-sm text-muted-foreground">
                                                                {format(sleepStart, 'HH:mm')} - {format(sleepEnd, 'HH:mm')} • {duration}h • ⭐ {entry.quality_rating}/10
                                                            </p>
                                                        </div>
                                                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => { e.stopPropagation(); handleDeleteSleep(entry.id); }}>
                                                                <Trash2 className="w-4 h-4 text-red-400" />
                                                            </Button>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </div>
            </motion.div>

            {/* Quick Log Drawer */}
            <AnimatePresence>
                {quickLogOpen && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
                            onClick={() => setQuickLogOpen(false)}
                        />
                        <motion.div
                            initial={{ y: '100%' }}
                            animate={{ y: 0 }}
                            exit={{ y: '100%' }}
                            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                            className="fixed bottom-0 left-0 right-0 z-50 bg-card rounded-t-3xl border-t border-border shadow-2xl max-h-[85vh] overflow-y-auto"
                        >
                            <div className="flex justify-center py-2">
                                <div className="w-12 h-1.5 bg-muted-foreground/20 rounded-full" />
                            </div>
                            
                            <div className="px-4 pb-32 space-y-3">
                                <div className="flex items-center justify-between">
                                    <h3 className="text-lg font-bold flex items-center gap-2">
                                        <Moon className="w-5 h-5 icon-sleep" />
                                        {language === 'de' ? 'Schlaf eintragen' : 'Log Sleep'}
                                    </h3>
                                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setQuickLogOpen(false)}>
                                        <X className="w-4 h-4" />
                                    </Button>
                                </div>

                                {/* Date */}
                                <div className="text-center py-1.5 px-3 rounded-xl bg-primary/10 text-primary text-sm font-medium">
                                    {selectedDate.toLocaleDateString(language === 'de' ? 'de-DE' : 'en-US', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <Label className="text-muted-foreground text-sm">{language === 'de' ? 'Eingeschlafen' : 'Fell asleep'}</Label>
                                        <Input
                                            type="time"
                                            value={quickForm.sleep_time}
                                            onChange={(e) => setQuickForm({...quickForm, sleep_time: e.target.value})}
                                            className="bg-muted border-2 border-border h-12 text-lg text-center font-semibold"
                                        />
                                    </div>
                                    <div>
                                        <Label className="text-muted-foreground text-sm">{language === 'de' ? 'Aufgewacht' : 'Woke up'}</Label>
                                        <Input
                                            type="time"
                                            value={quickForm.wake_time}
                                            onChange={(e) => setQuickForm({...quickForm, wake_time: e.target.value})}
                                            className="bg-muted border-2 border-border h-12 text-lg text-center font-semibold"
                                        />
                                    </div>
                                </div>

                                <div className="text-center text-xl font-bold text-primary">
                                    {estimatedDuration.toFixed(1)}h
                                </div>

                                <div>
                                    <Label className="text-muted-foreground text-sm">{language === 'de' ? 'Qualität' : 'Quality'}: {quickForm.quality_rating}/10</Label>
                                    <Slider
                                        value={[quickForm.quality_rating]}
                                        onValueChange={([v]) => setQuickForm({...quickForm, quality_rating: v})}
                                        min={1} max={10} step={1}
                                        className="py-3"
                                    />
                                </div>

                                <Button
                                    onClick={handleQuickLog}
                                    disabled={quickLoading}
                                    className="w-full h-14 bg-primary text-primary-foreground hover:bg-primary/90 rounded-xl text-base font-bold"
                                    data-testid="sleep-save-btn"
                                >
                                    {quickLoading ? (
                                        <Loader2 className="w-5 h-5 animate-spin" />
                                    ) : (
                                        <>
                                            <Check className="w-5 h-5 mr-2" />
                                            {language === 'de' ? 'OK — Speichern' : 'OK — Save'}
                                        </>
                                    )}
                                </Button>
                                <div className="h-4" />
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </div>
    );
}
